// Story 8.8 (ADO-2017), AC1/AC3: the free-delivery status for a depot, kept current
// with the basket. Feeds `FreeDeliveryProgress` (design system). The cart review page
// (`CartPage`) and the cart side panel (`CartSidePanel`) mount the two together:
//
//   const status = useFreeDeliveryStatus(depotId);
//   return <FreeDeliveryProgress status={status} variant="review" />;
//
// Behaviour:
//   - It reads `GET /basket/free-delivery-status` for `depotId`, and again whenever a
//     basket mutation has reconciled (the store's `lines` change with nothing pending).
//     It does not refetch on the optimistic update, which the server has not written yet.
//   - A depot switch or unmount aborts the in-flight request, and a response for any
//     depot but the current one is never reported, so a slow answer for depot A never
//     paints on depot B.
//   - It returns `null` ("hidden") while the first answer for a depot is loading, when
//     the read fails, and when there is no depot. Never a spinner or an error: the
//     message is advisory and the order goes ahead without it (AC2). While a refetch for
//     the same depot is in flight, the last answer stays up rather than flickering.
//   - Passing `null` (no depot, or a caller that is hidden) forgets the last answer, so the
//     next depot starts hidden until its own first answer.
//   - A `NONE` answer is returned as-is; `FreeDeliveryProgress` renders nothing for it.

import { useEffect, useState } from 'react';
import { getFreeDeliveryStatus, type FreeDeliveryStatus } from '../api/basket.client';
import { useBasketStore } from './useBasketStore';
import { cartTargetKey } from '../api/cart-target';
import { useCartTargetAt } from './sessionCart';

/** An answer, tagged with the cart it was read for (Story 8.4, ADO-2013: the depot AND the
 *  account, so an account change at the same Value Centre never shows the left one's). */
interface CartStatus {
  readonly cartKey: string;
  readonly status: FreeDeliveryStatus;
}

export function useFreeDeliveryStatus(depotId: string | null): FreeDeliveryStatus | null {
  const lines = useBasketStore((s) => s.lines);
  const idle = useBasketStore((s) => s.pending.size === 0);
  const cart = useCartTargetAt(depotId);
  const cartKey = cart === null ? null : cartTargetKey(cart);
  const [latest, setLatest] = useState<CartStatus | null>(null);
  // No depot drops the last answer (React's "adjust state while rendering" pattern), so a
  // caller that passes null while it is hidden, the cart side panel when closed, never
  // repaints an answer read before the basket changed under it.
  if (depotId === null && latest !== null) setLatest(null);

  useEffect(() => {
    if (cart === null || cartKey === null || !idle) return;
    const controller = new AbortController();
    getFreeDeliveryStatus(cart, { signal: controller.signal })
      .then((status) => {
        if (!controller.signal.aborted) setLatest({ cartKey, status });
      })
      .catch(() => {
        if (!controller.signal.aborted) setLatest(null);
      });
    return () => controller.abort();
    // `lines` is a trigger, not an input: a reconciled mutation changes it.
  }, [cart, cartKey, idle, lines]);

  return latest !== null && latest.cartKey === cartKey ? latest.status : null;
}
